/**
 * External Auditor Portal
 *
 * Designed for external auditors with multi-engagement workloads (one auditor
 * across many companies × frameworks). Default view is the **engagement
 * portfolio**, not a flat list of every control.
 *
 * Workflow:
 *   1. Auditor lands on Engagements tab → sees one card per active engagement
 *      (company × framework × audit period) with progress + status.
 *   2. Click an engagement → drill into the focused per-control review (the
 *      detail drawer + per-page Satisfy / Request Changes is unchanged).
 *   3. Once every control in an engagement is Satisfied → the framework cert
 *      can be issued from inside the engagement view.
 *   4. Issued Certificates tab shows all certs the auditor has issued, sortable
 *      by issue date / expiry / company.
 *
 * Blind audit: submitter identity is never shown.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  CheckSquare, FileText, RefreshCw, X, AlertTriangle, Clock,
  CheckCircle, Search, Award, Download, ChevronRight, Eye, ChevronLeft,
  Building2, Paperclip, Briefcase, Activity, Calendar,
} from 'lucide-react';
import { complianceExecutionApi, CompliancePage } from '@domains/compliance/execution/api/complianceExecution.api';
import PageInfoButton from '@/components/PageInfoButton';
import { notify } from '@/core/notifications/notify';
import { useModal, ConfirmModal } from '@components/modal';

interface PageRow extends CompliancePage {
  framework_name: string;
  framework_version?: string;
  company_id: number;
  company_name?: string;
  evidence_count?: number;
  evidence_verified?: number;
}

interface EvidenceItem {
  id: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  status: string;
  created_at: string;
  updated_at?: string;
  notes?: string;
}

interface DetailPayload extends PageRow {
  evidence: EvidenceItem[];
  template_fields?: any[];
  audit_trail?: { action: string; event_description: string; created_at: string }[];
}

interface CertRow {
  id: number;
  certificate_number: string;
  company_id: number;
  company_name?: string;
  framework_id: number;
  framework_name?: string;
  status: 'active' | 'expired' | 'revoked' | 'suspended';
  issue_date: string;
  expiry_date: string | null;
  overall_score?: number;
  auditor_id: number;
}

interface Engagement {
  key: string;
  companyId: number;
  companyName: string;
  frameworkId: number;
  frameworkName: string;
  frameworkVersionId?: number;
  frameworkVersion?: string;
  pages: PageRow[];
  cert?: CertRow;
}

const STATUS_CFG: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  approved_for_audit: {
    label: 'Ready for Audit',
    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: <CheckSquare size={11} />,
  },
  certified: {
    label: 'Certified',
    cls: 'bg-violet-100 text-violet-700 border-violet-200',
    icon: <Award size={11} />,
  },
};

type Tab = 'engagements' | 'certificates';
type EngagementFilter = 'all' | 'in_progress' | 'awaiting_cert' | 'certified';

const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const daysBetween = (a: Date, b: Date) => Math.floor((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));

const ExternalAuditorDashboard: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const { openModal } = useModal();

  // Data
  const [pages, setPages] = useState<PageRow[]>([]);
  const [existingCerts, setExistingCerts] = useState<CertRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Navigation
  const [tab, setTab] = useState<Tab>('engagements');
  const [selectedEngagementKey, setSelectedEngagementKey] = useState<string | null>(null);
  const [engagementFilter, setEngagementFilter] = useState<EngagementFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [certFilter, setCertFilter] = useState<'all' | 'active' | 'expiring' | 'expired'>('all');

  // Detail drawer state (per-control review)
  const [detail, setDetail] = useState<DetailPayload | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [action, setAction] = useState<'satisfy' | 'request_changes' | null>(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [issuingCertFor, setIssuingCertFor] = useState<string | null>(null);

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const [data, certData] = await Promise.all([
        complianceExecutionApi.getExternalAuditorPages(),
        fetch('/api/compliance/certifications', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .then(r => r.json()).then(j => (j?.data ?? []) as CertRow[]).catch(() => [] as CertRow[]),
      ]);
      setPages(data as PageRow[]);
      setExistingCerts(certData);
    } catch {
      setPages([]);
      setExistingCerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Build engagements (one per company × framework × frameworkVersion) merging in any existing cert
  const engagements: Engagement[] = useMemo(() => {
    const byKey = new Map<string, Engagement>();
    for (const p of pages) {
      const key = `${p.company_id}|${p.framework_id}|${p.framework_version_id ?? ''}`;
      const e = byKey.get(key) ?? {
        key,
        companyId: p.company_id,
        companyName: p.company_name ?? `Company #${p.company_id}`,
        frameworkId: p.framework_id,
        frameworkName: p.framework_name,
        frameworkVersionId: p.framework_version_id,
        frameworkVersion: p.framework_version,
        pages: [],
      };
      e.pages.push(p);
      byKey.set(key, e);
    }
    for (const e of byKey.values()) {
      e.cert = existingCerts
        .filter(c => c.company_id === e.companyId && c.framework_id === e.frameworkId && c.status !== 'revoked')
        .sort((a, b) => new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime())[0];
    }
    return Array.from(byKey.values()).sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [pages, existingCerts]);

  const selectedEngagement = useMemo(
    () => engagements.find(e => e.key === selectedEngagementKey) ?? null,
    [engagements, selectedEngagementKey],
  );

  // KPIs — auditor workload
  const kpis = useMemo(() => {
    const pendingDecisions = pages.filter(p => p.status === 'approved_for_audit').length;
    const issuedYTD = existingCerts.filter(c => {
      const d = new Date(c.issue_date);
      return d.getFullYear() === new Date().getFullYear();
    }).length;
    const inProgressEngagements = engagements.filter(e => !e.cert).length;
    const myCerts = existingCerts.filter(c => c.auditor_id === user?.id);
    return {
      activeEngagements: engagements.length,
      inProgressEngagements,
      pendingDecisions,
      issuedYTD,
      myCertCount: myCerts.length,
    };
  }, [pages, existingCerts, engagements, user?.id]);

  // Filtered engagements view
  const filteredEngagements = useMemo(() => {
    return engagements.filter(e => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (!(e.companyName.toLowerCase().includes(q) || e.frameworkName.toLowerCase().includes(q))) return false;
      }
      if (engagementFilter === 'all') return true;
      const ready = e.pages.filter(p => p.status === 'approved_for_audit').length;
      const certified = e.pages.filter(p => p.status === 'certified').length;
      if (engagementFilter === 'in_progress') return !e.cert && ready > 0;
      if (engagementFilter === 'awaiting_cert') return !e.cert && ready === 0 && certified === e.pages.length;
      if (engagementFilter === 'certified') return !!e.cert;
      return true;
    });
  }, [engagements, engagementFilter, searchTerm]);

  // Filtered certs view
  const filteredCerts = useMemo(() => {
    const now = new Date();
    return existingCerts
      .filter(c => {
        if (certFilter === 'all') return true;
        if (certFilter === 'active') return c.status === 'active';
        if (certFilter === 'expired') return c.status === 'expired' || (c.expiry_date && new Date(c.expiry_date) < now);
        if (certFilter === 'expiring') {
          if (!c.expiry_date) return false;
          const days = daysBetween(now, new Date(c.expiry_date));
          return days >= 0 && days <= 90 && c.status === 'active';
        }
        return true;
      })
      .filter(c => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (c.certificate_number || '').toLowerCase().includes(q)
          || (c.company_name || '').toLowerCase().includes(q)
          || (c.framework_name || '').toLowerCase().includes(q);
      })
      .sort((a, b) => new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime());
  }, [existingCerts, certFilter, searchTerm]);

  // ── Detail drawer + actions ──────────────────────────────────────────────

  const openDetail = async (p: PageRow) => {
    setDetail(null);
    setDetailLoading(true);
    setAction(null);
    setComments('');
    try {
      const d = await complianceExecutionApi.getExternalAuditorPageDetail(p.id);
      setDetail(d);
    } catch (e: any) {
      notify.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load page detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetail(null); setAction(null); setComments(''); };

  const fetchEvidenceBlob = async (evidenceId: number): Promise<Blob | null> => {
    try {
      const res = await fetch(`/api/compliance/execution/evidence/${evidenceId}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) {
        let msg = 'Failed to fetch evidence';
        try { const j = await res.json(); msg = j?.message || msg; } catch {}
        notify.error(msg);
        return null;
      }
      return await res.blob();
    } catch (e: any) {
      notify.error(e?.message ?? 'Failed to fetch evidence');
      return null;
    }
  };

  const previewEvidence = async (evidenceId: number) => {
    const blob = await fetchEvidenceBlob(evidenceId);
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  };

  const downloadEvidence = async (evidenceId: number, fileName: string) => {
    const blob = await fetchEvidenceBlob(evidenceId);
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const isPreviewable = (mime?: string) =>
    !!mime && (mime.startsWith('image/') || mime === 'application/pdf' || mime.startsWith('text/'));

  const submitDecision = async () => {
    if (!detail || !action) return;
    if (!comments.trim()) {
      notify.error('Please include a note explaining your decision.');
      return;
    }
    setSubmitting(true);
    try {
      if (action === 'satisfy') {
        await complianceExecutionApi.certifyPage(detail.id, comments);
        notify.success('Page marked satisfied.');
      } else {
        await complianceExecutionApi.requestChangesExternal(detail.id, comments);
        notify.success('Change request sent back to the company.');
      }
      closeDetail();
      await loadAll();
    } catch (e: any) {
      notify.error(e?.response?.data?.message ?? e?.message ?? 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const issueCertificate = (e: Engagement) => {
    if (!e.frameworkVersionId) {
      notify.error('Framework version is missing — cannot issue certificate.');
      return;
    }
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Issue Compliance Certificate',
        message:
          `Issue the certificate for ${e.companyName} — ${e.frameworkName}${e.frameworkVersion ? ` v${e.frameworkVersion}` : ''}?\n\n` +
          `All ${e.pages.length} controls have been reviewed and marked Satisfied. This action is FINAL:\n` +
          `• A certificate record will be created with a certificate number, issue date, and expiry (typically 3 years).\n` +
          `• The audit period will close.\n` +
          `• All pages remain LOCKED permanently as the immutable audit record.`,
        confirmText: 'Issue Certificate',
        cancelText: 'Cancel',
        type: 'success',
        onConfirm: async () => {
          setIssuingCertFor(e.key);
          try {
            const cert = await complianceExecutionApi.issueCertification({
              companyId: e.companyId,
              frameworkId: e.frameworkId,
              frameworkVersionId: e.frameworkVersionId!,
            });
            notify.success(`Certificate ${cert?.certificateNumber ?? cert?.certificate_number ?? 'issued'} — ${e.companyName} / ${e.frameworkName}.`);
            await loadAll();
          } catch (err: any) {
            notify.error(err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Failed to issue certificate');
          } finally {
            setIssuingCertFor(null);
          }
        },
        onCancel: () => {},
      },
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (!hasPermission('audit.dashboard.view') && !hasPermission('certifications.manage')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 p-8">
        <Award className="text-red-400 mb-4" size={48} />
        <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
        <p className="text-slate-500">External auditor permissions are required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-3xl font-bold text-slate-900">External Auditor Portal</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5">
              Independent compliance review · blind audit (submitter identity hidden)
            </p>
            <p className="text-xs text-blue-600 font-medium mt-1">
              {user?.first_name} {user?.last_name} · External Auditor
            </p>
          </div>
          <button
            onClick={loadAll}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50"
          >
            <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Active engagements', value: kpis.activeEngagements, sub: `${kpis.inProgressEngagements} in progress`, icon: <Briefcase size={18} className="text-sky-600" />, accent: 'text-sky-700' },
            { label: 'Pending decisions', value: kpis.pendingDecisions, sub: kpis.pendingDecisions > 0 ? 'awaiting your review' : 'all caught up', icon: <Clock size={18} className="text-amber-600" />, accent: 'text-amber-700' },
            { label: 'Certificates issued', value: kpis.issuedYTD, sub: 'this year', icon: <Award size={18} className="text-violet-600" />, accent: 'text-violet-700' },
            { label: 'Your certificates', value: kpis.myCertCount, sub: 'lifetime', icon: <CheckCircle size={18} className="text-emerald-600" />, accent: 'text-emerald-700' },
          ].map(k => (
            <div key={k.label} className="bg-white rounded-2xl border border-slate-100 p-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <div className="flex items-center gap-2 mb-1">{k.icon}<p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{k.label}</p></div>
              <p className={`text-3xl font-bold ${k.accent}`}>{k.value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{k.sub}</p>
            </div>
          ))}
        </div>

        {/* Tab strip */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 mb-4 w-fit">
          {([
            { k: 'engagements' as Tab, label: `Engagements (${engagements.length})` },
            { k: 'certificates' as Tab, label: `Issued Certificates (${existingCerts.length})` },
          ]).map(t => (
            <button
              key={t.k}
              onClick={() => { setTab(t.k); setSelectedEngagementKey(null); setSearchTerm(''); }}
              className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
                tab === t.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        ) : tab === 'engagements' && !selectedEngagement ? (
          <EngagementListView
            engagements={filteredEngagements}
            allEngagements={engagements}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            engagementFilter={engagementFilter}
            setEngagementFilter={setEngagementFilter}
            onOpen={key => setSelectedEngagementKey(key)}
          />
        ) : tab === 'engagements' && selectedEngagement ? (
          <EngagementDetailView
            engagement={selectedEngagement}
            onBack={() => setSelectedEngagementKey(null)}
            onOpenPage={openDetail}
            onIssueCertificate={() => issueCertificate(selectedEngagement)}
            issuingCertFor={issuingCertFor}
          />
        ) : (
          <CertificatesView
            certs={filteredCerts}
            allCount={existingCerts.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            certFilter={certFilter}
            setCertFilter={setCertFilter}
            currentUserId={user?.id}
          />
        )}
      </div>

      {/* Detail drawer */}
      {(detail || detailLoading) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="bg-slate-900 px-5 py-4 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {detail ? (
                  <>
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">
                      {detail.company_name} · {detail.framework_name}{detail.framework_version ? ` v${detail.framework_version}` : ''}
                    </p>
                    <h2 className="text-lg font-bold text-white mt-0.5">{detail.title}</h2>
                    <p className="text-xs text-slate-300 mt-0.5">{detail.section}</p>
                    {(detail.clause_ref || detail.source_ref) && (
                      <p className="text-[11px] text-slate-300 mt-1 font-mono">
                        {detail.clause_ref ? `Clause ${detail.clause_ref}` : null}
                        {detail.clause_ref && detail.source_ref && detail.source_ref !== detail.clause_ref ? ' · ' : null}
                        {detail.source_ref && detail.source_ref !== detail.clause_ref ? `Source ${detail.source_ref}` : null}
                        {!detail.clause_ref && detail.source_ref ? `Source ${detail.source_ref}` : null}
                      </p>
                    )}
                  </>
                ) : (
                  <h2 className="text-lg font-bold text-white">Loading…</h2>
                )}
              </div>
              <button onClick={closeDetail} className="text-white/70 hover:text-white ml-3 flex-shrink-0">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {detailLoading || !detail ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="animate-spin text-blue-600" size={32} />
                </div>
              ) : (
                <div className="px-5 py-5 space-y-5">
                  <section>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Requirement</h3>
                    <p className="text-sm text-slate-800 leading-relaxed">{detail.description || <span className="italic text-slate-400">No description</span>}</p>
                    {(detail.clause_ref || detail.source_ref) && (
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                          <span className="font-bold text-slate-600">Clause:</span>{' '}
                          <span className="font-mono text-slate-800">{detail.clause_ref || 'N/A'}</span>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                          <span className="font-bold text-slate-600">Source:</span>{' '}
                          <span className="font-mono text-slate-800">{detail.source_ref || detail.clause_ref || 'N/A'}</span>
                        </div>
                      </div>
                    )}
                    {detail.guidance && (
                      <div className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                        <span className="font-bold text-blue-900">Guidance:</span> <span className="text-blue-800">{detail.guidance}</span>
                      </div>
                    )}
                  </section>

                  <section>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Evidence files ({detail.evidence?.length ?? 0})</h3>
                    {(!detail.evidence || detail.evidence.length === 0) ? (
                      <p className="text-sm text-slate-400 italic">No evidence attached.</p>
                    ) : (
                      <div className="space-y-2">
                        {detail.evidence.map(ev => {
                          const statusCls =
                            ev.status === 'INTERNAL_VERIFIED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : ev.status === 'REJECTED' ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-slate-50 text-slate-600 border-slate-200';
                          const statusLabel =
                            ev.status === 'INTERNAL_VERIFIED' ? 'Internally verified'
                            : ev.status === 'REJECTED' ? 'Rejected'
                            : ev.status;
                          return (
                            <div key={ev.id} className="flex items-start justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                              <div className="flex items-start gap-2 min-w-0 flex-1">
                                <Paperclip size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-slate-800 truncate">{ev.file_name}</div>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusCls}`}>{statusLabel}</span>
                                    <span className="text-[10px] text-slate-400">{Math.round(ev.file_size / 1024)} KB</span>
                                    <span className="text-[10px] text-slate-400">{new Date(ev.created_at).toLocaleString()}</span>
                                  </div>
                                  {ev.notes && <p className="text-xs text-slate-600 mt-1 italic">"{ev.notes}"</p>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {isPreviewable(ev.mime_type) && (
                                  <button
                                    onClick={() => previewEvidence(ev.id)}
                                    className="flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 text-white border border-blue-600 rounded-md hover:bg-blue-700"
                                  >
                                    <Eye size={12} /> Preview
                                  </button>
                                )}
                                <button
                                  onClick={() => downloadEvidence(ev.id, ev.file_name)}
                                  className="flex items-center gap-1 px-2.5 py-1 text-xs bg-white text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
                                >
                                  <Download size={12} /> Download
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  {detail.internal_comments && (
                    <section>
                      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Internal auditor's last request-changes note</h3>
                      <div className="text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">{detail.internal_comments}</div>
                    </section>
                  )}

                  {detail.audit_trail && detail.audit_trail.length > 0 && (
                    <section>
                      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">History</h3>
                      <div className="space-y-1.5">
                        {detail.audit_trail.slice(0, 8).map((e, i) => (
                          <div key={i} className="text-xs text-slate-600 flex items-start gap-2">
                            <Clock size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="font-semibold">{e.action}</span> · {e.event_description}
                              <span className="text-slate-400 ml-2">{new Date(e.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {detail.status === 'approved_for_audit' && (
                    <section className="border-t border-slate-200 pt-5">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Your decision</h3>
                      {!action ? (
                        <div className="flex gap-2">
                          <button onClick={() => setAction('satisfy')} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                            <CheckCircle size={15} /> Mark Satisfied
                          </button>
                          <button onClick={() => setAction('request_changes')} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                            <AlertTriangle size={15} /> Request Changes
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-slate-600 mb-2">
                            {action === 'satisfy'
                              ? 'This control will be marked Certified. Once every control in the engagement is decided, you can issue the framework certificate.'
                              : 'The compliance team will be sent back to fix the evidence. Be specific — what is missing or wrong?'}
                          </p>
                          <textarea rows={4} value={comments} onChange={e => setComments(e.target.value)}
                            placeholder={action === 'satisfy' ? 'Confirm evidence meets the required standard…' : 'Explain what needs to be changed and why…'}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                          <div className="flex gap-2 mt-3">
                            <button onClick={() => { setAction(null); setComments(''); }} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Back</button>
                            <button onClick={submitDecision} disabled={submitting || !comments.trim()}
                              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 ${action === 'satisfy' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                              {submitting ? <RefreshCw size={14} className="animate-spin" /> : action === 'satisfy' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                              {submitting ? 'Submitting…' : action === 'satisfy' ? 'Confirm — Mark Satisfied' : 'Send back for Changes'}
                            </button>
                          </div>
                        </>
                      )}
                    </section>
                  )}
                  {detail.status === 'certified' && (
                    <section className="border-t border-slate-200 pt-5">
                      <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-3 flex items-center gap-3">
                        <Award className="text-violet-600" size={18} />
                        <div>
                          <div className="text-sm font-semibold text-violet-900">Control Certified</div>
                          <div className="text-xs text-violet-700">
                            This control has been marked Satisfied by an external auditor and is locked as part of the certified record.
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Sub-views
// ──────────────────────────────────────────────────────────────────────────

const EngagementListView: React.FC<{
  engagements: Engagement[];
  allEngagements: Engagement[];
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  engagementFilter: EngagementFilter;
  setEngagementFilter: (f: EngagementFilter) => void;
  onOpen: (key: string) => void;
}> = ({ engagements, allEngagements, searchTerm, setSearchTerm, engagementFilter, setEngagementFilter, onOpen }) => {
  const counts = useMemo(() => {
    const c = { all: allEngagements.length, in_progress: 0, awaiting_cert: 0, certified: 0 };
    for (const e of allEngagements) {
      const ready = e.pages.filter(p => p.status === 'approved_for_audit').length;
      const certified = e.pages.filter(p => p.status === 'certified').length;
      if (e.cert) c.certified++;
      else if (ready === 0 && certified === e.pages.length && e.pages.length > 0) c.awaiting_cert++;
      else c.in_progress++;
    }
    return c;
  }, [allEngagements]);

  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-100 p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search company or framework…"
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div className="bg-slate-100 rounded-lg p-0.5 flex gap-1">
          {([
            { k: 'all' as EngagementFilter, label: `All (${counts.all})` },
            { k: 'in_progress' as EngagementFilter, label: `In progress (${counts.in_progress})` },
            { k: 'awaiting_cert' as EngagementFilter, label: `Awaiting cert (${counts.awaiting_cert})` },
            { k: 'certified' as EngagementFilter, label: `Certified (${counts.certified})` },
          ]).map(f => (
            <button
              key={f.k}
              onClick={() => setEngagementFilter(f.k)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md ${
                engagementFilter === f.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {engagements.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-16 text-center">
          <Briefcase className="text-slate-300 mb-4 mx-auto" size={48} />
          <h3 className="text-lg font-semibold text-slate-600 mb-1">No engagements in this view</h3>
          <p className="text-slate-400 text-sm">Try clearing filters or search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {engagements.map(e => {
            const ready = e.pages.filter(p => p.status === 'approved_for_audit').length;
            const certified = e.pages.filter(p => p.status === 'certified').length;
            const total = e.pages.length;
            const pct = total > 0 ? Math.round((certified / total) * 100) : 0;
            const statusLabel = e.cert ? 'Certificate Issued' : ready === 0 && certified === total ? 'Awaiting Certificate' : 'In Progress';
            const statusCls = e.cert ? 'bg-violet-100 text-violet-700 border-violet-200' : ready === 0 && certified === total ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200';
            const StatusIcon = e.cert ? Award : ready === 0 ? CheckCircle : Clock;
            return (
              <button
                key={e.key}
                onClick={() => onOpen(e.key)}
                className="bg-white rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all p-4 text-left flex flex-col"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">
                      <Building2 size={12} className="text-slate-400" />
                      <span className="font-semibold text-slate-700 truncate">{e.companyName}</span>
                    </div>
                    <h3 className="font-bold text-slate-800 text-base">
                      {e.frameworkName}{e.frameworkVersion ? <span className="text-slate-400 font-medium text-xs ml-1.5">v{e.frameworkVersion}</span> : null}
                    </h3>
                  </div>
                  <span className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full border ${statusCls} flex-shrink-0`}>
                    <StatusIcon size={10} /> {statusLabel}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
                  <span>{total} control{total !== 1 ? 's' : ''}</span>
                  {ready > 0 && <span className="text-amber-600 font-semibold">{ready} awaiting decision</span>}
                  <span className="text-violet-600">{certified} certified</span>
                </div>

                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-3">
                  <div className={`h-full ${e.cert ? 'bg-violet-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                </div>

                {e.cert && (
                  <div className="text-[11px] text-slate-500 mb-2 bg-violet-50 border border-violet-100 rounded-md px-2 py-1.5">
                    <span className="font-bold text-violet-900">{e.cert.certificate_number}</span>
                    {e.cert.expiry_date && <span className="ml-2">expires {fmtDate(e.cert.expiry_date)}</span>}
                  </div>
                )}

                <div className="flex items-center justify-between mt-auto pt-1">
                  <span className="text-xs text-slate-400">Click to {e.cert ? 'review' : ready > 0 ? 'continue review' : 'open'}</span>
                  <ChevronRight size={16} className="text-blue-500" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EngagementDetailView: React.FC<{
  engagement: Engagement;
  onBack: () => void;
  onOpenPage: (p: PageRow) => void;
  onIssueCertificate: () => void;
  issuingCertFor: string | null;
}> = ({ engagement: e, onBack, onOpenPage, onIssueCertificate, issuingCertFor }) => {
  const ready = e.pages.filter(p => p.status === 'approved_for_audit').length;
  const certified = e.pages.filter(p => p.status === 'certified').length;
  const allCertified = e.pages.length > 0 && certified === e.pages.length;
  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-3">
        <ChevronLeft size={16} /> Back to engagements
      </button>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4 bg-slate-50">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-0.5">
              <Building2 size={14} className="text-slate-400" />
              <span className="font-semibold text-slate-700">{e.companyName}</span>
            </div>
            <h2 className="text-lg font-bold text-slate-800">
              {e.frameworkName}{e.frameworkVersion ? <span className="text-slate-400 font-medium text-sm ml-2">v{e.frameworkVersion}</span> : null}
            </h2>
            <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-500">
              <span>{e.pages.length} controls</span>
              {ready > 0 && <span className="text-amber-600">{ready} awaiting</span>}
              <span className="text-violet-600">{certified} certified</span>
            </div>
          </div>
          {e.cert ? (
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-200 rounded-lg">
                <Award size={14} /> Certificate issued
              </span>
              <span className="text-[11px] text-slate-500">
                {e.cert.certificate_number}{e.cert.expiry_date ? ` · expires ${fmtDate(e.cert.expiry_date)}` : ''}
              </span>
            </div>
          ) : allCertified ? (
            <button
              onClick={onIssueCertificate}
              disabled={issuingCertFor === e.key}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex-shrink-0"
            >
              <Award size={16} />
              {issuingCertFor === e.key ? 'Issuing…' : 'Issue Certificate'}
            </button>
          ) : (
            <span className="text-xs text-slate-500 bg-white border border-slate-200 px-3 py-1.5 rounded-lg flex-shrink-0">
              {ready} awaiting review
            </span>
          )}
        </div>

        <div className="divide-y divide-slate-100">
          {e.pages.map(p => {
            const cfg = STATUS_CFG[p.status] ?? { label: p.status, cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: <FileText size={11} /> };
            return (
              <div key={p.id} className="px-5 py-4 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.cls}`}>
                      {cfg.icon} {cfg.label}
                    </span>
                    <span className="text-xs text-slate-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">{p.section}</span>
                    {p.priority && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        p.priority === 'high' || p.priority === 'critical' ? 'bg-red-50 text-red-600 border border-red-200'
                        : p.priority === 'medium' ? 'bg-amber-50 text-amber-600 border border-amber-200'
                        : 'bg-slate-50 text-slate-500 border border-slate-200'
                      }`}>{p.priority}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mt-1">{p.title}</h3>
                  {p.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
                    {typeof p.evidence_count === 'number' && (
                      <span className="flex items-center gap-1">
                        <Paperclip size={10} /> {p.evidence_count} evidence
                        {typeof p.evidence_verified === 'number' && p.evidence_verified > 0 && (
                          <span className="text-emerald-600">({p.evidence_verified} verified)</span>
                        )}
                      </span>
                    )}
                    {p.updated_at && <span className="flex items-center gap-1"><Clock size={10} /> {new Date(p.updated_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <button
                  onClick={() => onOpenPage(p)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg flex-shrink-0 ${
                    p.status === 'certified' ? 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50' : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  title={p.status === 'certified' ? 'View the decision and audit trail for this certified control' : 'Review evidence and decide'}
                >
                  <Eye size={13} /> {p.status === 'certified' ? 'View' : 'Review'} <ChevronRight size={13} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CertificatesView: React.FC<{
  certs: CertRow[];
  allCount: number;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  certFilter: 'all' | 'active' | 'expiring' | 'expired';
  setCertFilter: (f: 'all' | 'active' | 'expiring' | 'expired') => void;
  currentUserId?: number;
}> = ({ certs, allCount, searchTerm, setSearchTerm, certFilter, setCertFilter, currentUserId }) => {
  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-100 p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search certificate number, company, or framework…"
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div className="bg-slate-100 rounded-lg p-0.5 flex gap-1">
          {([
            { k: 'all' as const, label: `All (${allCount})` },
            { k: 'active' as const, label: 'Active' },
            { k: 'expiring' as const, label: 'Expiring ≤90d' },
            { k: 'expired' as const, label: 'Expired' },
          ]).map(f => (
            <button key={f.k} onClick={() => setCertFilter(f.k)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md ${certFilter === f.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {certs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-16 text-center">
          <Award className="text-slate-300 mb-4 mx-auto" size={48} />
          <h3 className="text-lg font-semibold text-slate-600 mb-1">No certificates here</h3>
          <p className="text-slate-400 text-sm">Issued certificates appear here once you certify all controls of an engagement.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Certificate</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Framework</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Issued</th>
                  <th className="px-4 py-3">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {certs.map(c => {
                  const now = new Date();
                  const expDate = c.expiry_date ? new Date(c.expiry_date) : null;
                  const days = expDate ? daysBetween(now, expDate) : null;
                  const expCls = !expDate ? 'text-slate-500' : days! < 0 ? 'text-red-600 font-semibold' : days! <= 30 ? 'text-red-500 font-semibold' : days! <= 90 ? 'text-amber-600 font-semibold' : 'text-slate-600';
                  const statusCls =
                    c.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : c.status === 'expired' ? 'bg-red-50 text-red-700 border-red-200'
                    : c.status === 'revoked' ? 'bg-slate-100 text-slate-600 border-slate-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200';
                  return (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-800">{c.certificate_number}</div>
                        {c.auditor_id === currentUserId && <div className="text-[10px] text-blue-600 font-semibold mt-0.5">Issued by you</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{c.company_name ?? `#${c.company_id}`}</td>
                      <td className="px-4 py-3 text-slate-700">{c.framework_name ?? `#${c.framework_id}`}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full border ${statusCls}`}>{c.status}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(c.issue_date)}</td>
                      <td className={`px-4 py-3 whitespace-nowrap ${expCls}`}>
                        {fmtDate(c.expiry_date)}
                        {days !== null && <span className="text-[10px] block">{days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalAuditorDashboard;
